/**
 * Turns the raw strings the auth flow throws — Auth0 error codes, fetch
 * failures, our own guard messages — into one sentence a visitor can act on.
 *
 * Login.vue used to print `session.errorMessage` verbatim, so a visitor saw
 * "invalid_grant" or "Failed to fetch" under the sign-in button. The raw
 * text is still kept (the caller puts it in a `title`) for anyone debugging.
 */
import { i18n } from "@/plugins/i18n";

const MAX_PASSTHROUGH_CHARS = 140;

const RULES: { test: RegExp; key: string }[] = [
  {
    test: /access_denied|unauthori[sz]ed|not authori[sz]ed|user is blocked|blocked/i,
    key: "auth.errors.denied",
  },
  {
    test: /invalid_grant|login_required|interaction_required|consent_required|expired/i,
    key: "auth.errors.expired",
  },
  {
    test: /state mismatch|no transaction state/i,
    key: "auth.errors.state",
  },
  {
    test: /failed to fetch|networkerror|network request failed|load failed|timed out|timeout/i,
    key: "auth.errors.network",
  },
  { test: /token exchange failed|failed to fetch user info|authentication failed/i, key: "auth.errors.generic" },
  { test: /could not restore your session/i, key: "auth.errors.restore" },
  // PAC-1387 — an API failure as `apiRequest` formats it ("GET /path failed:
  // 403 …"). Never a sentence for a visitor: the detail goes behind the
  // "Details" disclosure instead. Last, so the specific rules above still win.
  { test: /^(GET|POST|PUT|PATCH|DELETE) \S+ failed: \d{3}/, key: "auth.errors.generic" },
];

/** True when the friendly sentence hides something worth showing under "Details". */
export function hasAuthErrorDetail(raw: string | null | undefined): boolean {
  const text = (raw ?? "").trim();
  return !!text && humaniseAuthError(text) !== text;
}

export function humaniseAuthError(raw: string | null | undefined): string {
  const text = (raw ?? "").trim();
  if (!text) return i18n.global.t("auth.errors.generic");
  const rule = RULES.find((r) => r.test.test(text));
  if (rule) return i18n.global.t(rule.key);
  // Auth0's own `error_description` is often already a sentence ("Wrong
  // email or password.") — keep it, but never let a stack-trace-length
  // string onto the page.
  return text.length > MAX_PASSTHROUGH_CHARS ? `${text.slice(0, MAX_PASSTHROUGH_CHARS - 1)}…` : text;
}
